import React, { useEffect, useState } from 'react';
import { TabContent, TabPane, Spinner } from 'reactstrap';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchProductDetails } from '../../../services/Http/Api';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import TabBar from '../../../shared/layout/tabs/TabBar';
import TabButton from '../../../shared/layout/tabs/TabButton';
import StyledCard from '../../../shared/layout/cards/StyledCard';
import { MdDescription, MdHistory, MdInfo, MdSecurity } from 'react-icons/md';
import styled from 'styled-components';
import TransactionsTable from '../Items/Item/TransactionsTable';
import { useCurrency } from '../../../context/CurrencyProvider';

const ProductView = () => {
    const { productId } = useParams();
    const { t } = useTranslation();
    const { formatResolvedAmount } = useCurrency();
    const [activeTab, setActiveTab] = useState('1');
    const [productDetails, setProductDetails] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const getProductDetails = async () => {
            setLoading(true);
            try {
                const data = await fetchProductDetails(productId);
                setProductDetails(data);
            } catch (error) {
                console.error('Error fetching product details:', error);
            } finally {
                setLoading(false);
            }
        };

        getProductDetails();
    }, [productId]);

    const toggleTab = (tab) => {
        if (activeTab !== tab) setActiveTab(tab);
    };

    return (
        <PageContainer>
            <PageHeader>
                <PageTitle>
                    {productDetails ? productDetails.product_name : t('Items.ViewItem.loading')}
                </PageTitle>
            </PageHeader>
            <PageContent>
                <TabBar>
                    <TabButton
                        active={activeTab === '1'}
                        onClick={() => toggleTab('1')}
                        icon={<MdDescription />}
                    >
                        {t('Items.ViewItem.tabs.description')}
                    </TabButton>
                    <TabButton
                        active={activeTab === '2'}
                        onClick={() => toggleTab('2')}
                        icon={<MdHistory />}
                    >
                        {t('Items.ViewItem.tabs.transactions')}
                    </TabButton>
                    <TabButton
                        active={activeTab === '3'}
                        onClick={() => toggleTab('3')}
                        icon={<MdInfo />}
                    >
                        {t('Items.ViewItem.tabs.details')}
                    </TabButton>
                    <TabButton
                        active={activeTab === '4'}
                        onClick={() => toggleTab('4')}
                        icon={<MdSecurity />}
                    >
                        {t('Items.ViewItem.tabs.warranty')}
                    </TabButton>
                </TabBar>
                <ContentSurface>
                    <TabContent activeTab={activeTab}>
                    <TabPane tabId="1">
                        <StyledCard>
                            {loading ? (
                                <SpinnerWrapper>
                                    <Spinner />
                                </SpinnerWrapper>
                            ) : (
                                <ProductSection>
                                    <ImageContainer>
                                        <ProductImage
                                            src={productDetails?.image_url || ''}
                                            alt={productDetails?.product_name || ''}
                                        />
                                        {productDetails?.manufacturer?.logo && (
                                            <ManufacturerLogo
                                                src={productDetails.manufacturer.logo}
                                                alt={productDetails.manufacturer.name || ''}
                                            />
                                        )}
                                    </ImageContainer>
                                    <DescriptionContainer>
                                        <h3>{productDetails?.product_name}</h3>
                                        {productDetails?.description && (
                                            <Description dangerouslySetInnerHTML={{ __html: productDetails.description }} />
                                        )}
                                        <p>
                                            <strong>{t('Items.ViewItem.manufacturer')}</strong>{' '}
                                            {productDetails?.manufacturer?.name || '—'}
                                        </p>
                                        <p>
                                            <strong>{t('Items.ViewItem.category')}</strong>{' '}
                                            {productDetails?.categoryCore?.name || '—'}
                                        </p>
                                    </DescriptionContainer>
                                </ProductSection>
                            )}
                        </StyledCard>
                    </TabPane>
                    <TabPane tabId="2">
                        <StyledCard>
                            <TransactionsTable itemId={productId} />
                        </StyledCard>
                    </TabPane>
                    <TabPane tabId="3">
                        <StyledCard>
                            {loading ? (
                                <SpinnerWrapper>
                                    <Spinner />
                                </SpinnerWrapper>
                            ) : (
                                <DetailsSection>
                                    <DetailRow>
                                        <DetailLabel>{t('Items.ViewItem.details.model_number')}</DetailLabel>
                                        <DetailValue>{productDetails?.model_number || '—'}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                        <DetailLabel>{t('Items.ViewItem.details.release_year')}</DetailLabel>
                                        <DetailValue>{productDetails?.release_year || 'N/A'}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                        <DetailLabel>{t('Items.ViewItem.details.initial_price')}</DetailLabel>
                                        <DetailValue>
                                            {formatResolvedAmount({
                                                eurAmount: productDetails?.initial_price,
                                                localAmount: productDetails?.initial_price_local,
                                                localCurrencyId: productDetails?.currency_id,
                                            })}
                                        </DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                        <DetailLabel>{t('Items.ViewItem.details.source_id')}</DetailLabel>
                                        <DetailValue>{productDetails?.source_id || '—'}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                        <DetailLabel>{t('Items.ViewItem.details.icecat_id')}</DetailLabel>
                                        <DetailValue>{productDetails?.icecat_id || '—'}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                        <DetailLabel>{t('Items.ViewItem.details.category')}</DetailLabel>
                                        <DetailValue>{productDetails?.categoryCore?.name || '—'}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                        <DetailLabel>{t('Items.ViewItem.details.technical_specifications')}</DetailLabel>
                                        <DetailValue>
                                            {productDetails?.technical_specifications
                                                ? Object.entries(
                                                    typeof productDetails.technical_specifications === 'string'
                                                        ? JSON.parse(productDetails.technical_specifications)
                                                        : productDetails.technical_specifications
                                                ).map(([key, val]) => (
                                                    <SpecItem key={key}>
                                                        <SpecKey>{key}:</SpecKey>
                                                        <SpecVal>{val}</SpecVal>
                                                    </SpecItem>
                                                ))
                                                : '—'}
                                        </DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                        <DetailLabel>{t('Items.ViewItem.details.certifications')}</DetailLabel>
                                        <DetailValue>
                                            {productDetails?.certifications ? JSON.stringify(productDetails.certifications) : 'N/A'}
                                        </DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                        <DetailLabel>{t('Items.ViewItem.details.material_composition')}</DetailLabel>
                                        <DetailValue>{productDetails?.material_composition || 'N/A'}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                        <DetailLabel>{t('Items.ViewItem.details.carbon_footprint')}</DetailLabel>
                                        <DetailValue>{productDetails?.carbon_footprint || 'N/A'}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                        <DetailLabel>{t('Items.ViewItem.details.recycling_instructions')}</DetailLabel>
                                        <DetailValue>{productDetails?.recycling_instructions || 'N/A'}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                        <DetailLabel>{t('Items.ViewItem.details.documentation')}</DetailLabel>
                                        <DetailValue>
                                            {productDetails?.documentation_url ? (
                                                <a href={productDetails.documentation_url} target="_blank" rel="noopener noreferrer">
                                                    {t('Items.ViewItem.details.view_documentation')}
                                                </a>
                                            ) : (
                                                '—'
                                            )}
                                        </DetailValue>
                                    </DetailRow>
                                </DetailsSection>
                            )}
                        </StyledCard>
                    </TabPane>
                    <TabPane tabId="4">
                        <StyledCard>
                            {loading ? (
                                <SpinnerWrapper>
                                    <Spinner />
                                </SpinnerWrapper>
                            ) : (
                                <WarrantySection>
                                    <h4>{t('Items.ViewItem.warranty.title')}</h4>
                                    <p><strong>{t('Items.ViewItem.warranty.name')}</strong> {productDetails?.warranty_template?.name || '—'}</p>
                                    <p><strong>{t('Items.ViewItem.warranty.duration')}</strong> {productDetails?.warranty_template?.duration_months || '—'} months</p>
                                    <p><strong>{t('Items.ViewItem.warranty.coverage')}</strong> {productDetails?.warranty_template?.terms?.coverage ? JSON.stringify(productDetails.warranty_template.terms.coverage) : '—'}</p>
                                    <p><strong>{t('Items.ViewItem.warranty.exclusions')}</strong> {productDetails?.warranty_template?.terms?.exclusions?.join(', ') || '—'}</p>
                                    <p><strong>{t('Items.ViewItem.warranty.claim_process')}</strong> {productDetails?.warranty_template?.claim_process?.process || '—'}</p>
                                    <p><strong>{t('Items.ViewItem.warranty.contact')}</strong> {productDetails?.warranty_template?.claim_process?.contact || '—'}</p>
                                    <p><strong>{t('Items.ViewItem.warranty.applicable_to')}</strong> {productDetails?.warranty_template?.applicable_to?.regions?.join(', ') || '—'}</p>
                                    <p><strong>{t('Items.ViewItem.warranty.active')}</strong> {productDetails?.warranty_template?.is_active ? 'Yes' : 'No'}</p>
                                </WarrantySection>
                            )}
                        </StyledCard>
                    </TabPane>
                    </TabContent>
                </ContentSurface>
            </PageContent>
        </PageContainer>
    );
};

export default ProductView;

const ProductSection = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${({ theme }) => theme?.spacing?.medium || '1.25rem'};
    padding: ${({ theme }) => theme?.spacing?.large || '2.5rem'};

    @media (min-width: ${({ theme }) => theme?.breakpoints?.mobile || '768px'}) {
        flex-direction: row;
        align-items: flex-start;
    }
`;

const ImageContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${({ theme }) => theme?.spacing?.small || '0.625rem'};
    background: rgba(130, 22, 146, 0.04);
    padding: 1rem;
    border-radius: 12px;
`;

const ProductImage = styled.img`
    width: 100%;
    max-width: 300px;
    height: auto;
    border-radius: 8px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
`;

const ManufacturerLogo = styled.img`
    width: 100px;
    height: auto;
    border-radius: 8px;
`;

const DescriptionContainer = styled.div`
  flex: 1;
  text-align: left;
  background: #fff;
  border-radius: 12px;
  padding: 1.125rem 1.25rem;
  border: 1px solid rgba(130, 22, 146, 0.12);

  h3 {
    font-size: ${({ theme }) => theme?.fontSizes?.large || '1.5rem'};
    color: ${({ theme }) => theme?.colors?.brand || 'rgba(130, 22, 146, 1)'};
    margin-bottom: 0.75rem;
  }

  p {
    font-size: ${({ theme }) => theme?.fontSizes?.medium || '1.1rem'};
    margin-bottom: 0.5rem;
    color: ${({ theme }) => theme?.colors?.textSecondary || '#4b5563'};
  }
`;

const Description = styled.div`
    font-size: ${({ theme }) => theme?.fontSizes?.medium || '1.1rem'};
    line-height: 1.6;
    margin-bottom: ${({ theme }) => theme?.spacing?.medium || '1.25rem'};
    color: ${({ theme }) => theme?.colors?.textSecondary || '#5a5a5a'};
    background: rgba(17, 24, 39, 0.02);
    padding: 0.75rem 0.875rem;
    border-radius: 10px;
    border: 1px solid rgba(0, 0, 0, 0.05);
`;

const DetailsSection = styled.div`
    display: grid;
    gap: ${({ theme }) => theme?.spacing?.small || '0.625rem'};
    padding: ${({ theme }) => theme?.spacing?.medium || '1.25rem'};
    background: #fff;
    border-radius: 12px;
    border: 1px solid rgba(0, 0, 0, 0.06);
`;

const DetailRow = styled.div`
    display: flex;
    justify-content: space-between;
    padding: ${({ theme }) => theme?.spacing?.tiny || '0.3125rem'} 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    gap: 1rem;
`;

const DetailLabel = styled.strong`
    flex: 1;
    font-size: ${({ theme }) => theme?.fontSizes?.medium || '1.05rem'};
    color: #1f2937;
`;

const DetailValue = styled.span`
    flex: 2;
    text-align: right;
    font-size: ${({ theme }) => theme?.fontSizes?.medium || '1.1rem'};
    color: ${({ theme }) => theme?.colors?.textSecondary || '#4b5563'};
`;

const SpecItem = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: ${({ theme }) => theme?.spacing?.tiny || '0.3125rem'};
    padding: 0.375rem 0;
    border-bottom: 1px dashed rgba(0, 0, 0, 0.08);
`;

const SpecKey = styled.strong`
    flex: 1;
    font-size: ${({ theme }) => theme?.fontSizes?.small || '0.9rem'};
    color: #1f2937;
`;

const SpecVal = styled.span`
    flex: 2;
    text-align: right;
    font-size: ${({ theme }) => theme?.fontSizes?.small || '0.9rem'};
    color: ${({ theme }) => theme?.colors?.textSecondary || '#4b5563'};
`;

const WarrantySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme?.spacing?.small || '0.625rem'};
  padding: ${({ theme }) => theme?.spacing?.medium || '1.25rem'};
  background: #fff;
  border-radius: 12px;
  border: 1px solid rgba(130, 22, 146, 0.12);

  h4 {
    font-size: ${({ theme }) => theme?.fontSizes?.large || '1.5rem'};
    color: ${({ theme }) => theme?.colors?.brand || 'rgba(130, 22, 146, 1)'};
    margin-bottom: 0.75rem;
  }

  p {
    font-size: ${({ theme }) => theme?.fontSizes?.medium || '1.1rem'};
    margin-bottom: ${({ theme }) => theme?.spacing?.small || '0.625rem'};
    color: ${({ theme }) => theme?.colors?.textSecondary || '#4b5563'};
  }
`;

const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
`;

const ContentSurface = styled.div`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 6px 16px rgba(17, 24, 39, 0.06);
  padding: 1rem;

  .tab-content {
    margin-top: 0.75rem;
  }

  .tab-pane {
    animation: fadeIn 0.2s ease;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(6px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;
